/**
 * Case-centric rather than anomaly-centric: a case is the unit a human works, so it carries
 * the triage state, the pre-validation the system did before bothering anyone, and enough
 * commercial context to decide whether it is worth chasing.
 *
 * Prioritisation is deliberately NOT a dollar figure. Estimated financial impact is easy to
 * misrepresent, so cases rank on things we can defend: how heavily the affected rate is
 * actually booked, and what tier the property sits in.
 */

export type CaseStatus = 'pending' | 'acknowledged' | 'resolved' | 'dismissed' | 'auto_resolved' | 'suppressed';
export type PropertyTier = 'tier_1' | 'tier_2' | 'tier_3';
export type PreValidationVerdict = 'confirmed' | 'self_resolved' | 'inconclusive' | 'not_run';

export interface PreValidationCheck {
  /** what was checked */
  label: string;
  /** did the check support the alert (true = problem still there) */
  supportsAlert: boolean | null;
  detail: string;
}

/** One affected product inside a case - what the case is actually made of. */
export interface AffectedProduct {
  dailyState?: Array<PropertyAriDay>;
  roomTypeCode: string;
  roomTypeName: string;
  ratePlanCode: string;
  ratePlanName: string;
  rateRank: number | null;
  bookings12m: number | null;
  hasMealPlan: boolean;
  nightsAffected: number;
  firstStayDate: string;
  lastStayDate: string;
  /** last rate we saw for this product, if any - null is the whole point of the alert */
  lastKnownRate: number | null;
  lastRateSeenAt: string | null;
}

export interface PropertyAriDay {
  unavailable_flag: boolean;
  stay_date: string;
  live_products: number;
  sellable_products: number;
  closed_products: number;
  blocked_products: number;
  unpriced_stocked_products: number;
  zero_inventory_products: number;
  missing_products: number;
  unknown_products: number;
}

export interface CaseEvent {
  at: string;
  actor: string;
  summary: string;
}

/** One-line gloss per rule, surfaced on hover rather than taking up layout. */
export const RULE_DESCRIPTIONS: Record<string, string> = {
  sellable_without_rate: 'The room is open and we hold inventory, but no rate is loaded — so it cannot be sold.',
  rate_blackout: 'This rate was priced across a long stretch of dates and now has no pricing at all.',
  coverage_shrink: 'Rate plans that used to arrive in the feed have stopped appearing for this room.',
  push_silence: 'The channel manager has stopped sending ARI updates for this property.',
  inventory_blackout: 'Inventory that used to be loaded has gone to zero across the horizon.',
  horizon_shrink: 'This property used to be priced months ahead and is now only priced weeks ahead.',
};

export interface AnomalyCase {
  entityGrain?: string;
  ruleDescription?: string | null;
  caseId: string;
  status: CaseStatus;
  ruleKey: string;
  ruleLabel: string;

  channel: string;
  chain: string;
  vendorBrand: string;
  territory: string;

  propertyName: string;
  propertyTier: PropertyTier | null;
  /** svc-reservation ids - what the ARI logs carry */
  propertyId: string;
  roomTypeId: string;
  roomRateId: string;
  vendorId: string;
  /** svc-accommodation id, a different id space - needed for the rates deep link */
  accommodationPropertyId: string;

  hotelCode: string;
  roomTypeName: string;
  ratePlanName: string;
  roomTypeCode: string;
  ratePlanCode: string;

  /** rank of this room+rate by bookings over the trailing 12 months, 1 = most booked */
  rateRank: number | null;
  bookings12m: number | null;
  /** does this rate include a meal plan or package inclusions */
  hasMealPlan: boolean;
  /** is this the lead rate on the offer */
  isLeadRate: boolean;

  nightsAffected: number;
  firstStayDate: string;
  lastStayDate: string;

  preValidation: {
    verdict: PreValidationVerdict;
    checks: Array<PreValidationCheck>;
    ranAt: string;
    /** counted per case by the store, since checks that cannot speak to a grain are excluded */
    checksAgreeing?: number;
    checksApplicable?: number;
  };

  firstSeenAt: string;
  lastSeenAt: string;
  occurrences: number;
  dismissReason?: string;

  /** every product this case covers - the row-level summary shows the worst of these */
  affectedProducts: Array<AffectedProduct>;
  history: Array<CaseEvent>;
}

/** Reusable dismissal reasons, so recurring known issues are one click and stay measurable. */
export const KNOWN_ISSUES: Array<{ key: string; label: string }> = [
  { key: 'fake_agoda_rate', label: 'Fake Agoda rate — not a real LPC product' },
  { key: 'seasonal_closure', label: 'Seasonal closure — property intentionally shut' },
  { key: 'contract_ended', label: 'Contract ended / property offboarding' },
  { key: 'rate_retired', label: 'Rate plan intentionally retired' },
  { key: 'test_property', label: 'Test or staging property' },
  { key: 'other', label: 'Other (explain)' },
];

export const TIER_LABELS: Record<PropertyTier, string> = {
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  tier_3: 'Tier 3',
};
